import type { JoinResponse, Product, BasketProduct, BasketResponse, WishProduct, WishProductsResponse, ProductDetail, ProductDetailResponse, ProductDetailProductOption, ComboProducts } from '../types/api';

/**
 * Mock products for demo mode (SPEC-030).
 * Conforms to Product type. Uses Unsplash images for visual rendering.
 */
export const MOCK_PRODUCTS: Product[] = [
  {
    id: 1,
    title: 'Classic Leather Handbag',
    price: '1,299.00',
    compare_at_price: '1,599.00',
    image_url: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&h=300&fit=crop',
    collection: 'Spring Collection',
  },
  {
    id: 2,
    title: 'Designer Sunglasses',
    price: '599.00',
    image_url: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400&h=300&fit=crop',
    collection: 'Accessories',
  },
  {
    id: 3,
    title: 'Silk Evening Dress',
    price: '2,499.00',
    compare_at_price: '3,299.00',
    image_url: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=300&fit=crop',
    collection: 'Evening Wear',
  },
];

/**
 * Mock basket products for demo mode (SPEC-032).
 * Uses MOCK_PRODUCTS[0] as the product reference.
 */
export const MOCK_BASKET_PRODUCTS: BasketProduct[] = [
  {
    id: 1,
    quantity: 1,
    discount_val: 0,
    product: MOCK_PRODUCTS[0],
    product_variant: {
      id: 101,
      title: 'Classic Leather Handbag - Black',
      price: '1,299.00',
      sku: 'CLH-BLK-001',
    },
  },
];

/**
 * Mock basket response for demo mode (SPEC-032).
 */
export const MOCK_BASKET_RESPONSE: BasketResponse = {
  products: MOCK_BASKET_PRODUCTS,
  total: '1,299.00',
};

/**
 * Mock wish products for demo mode (SPEC-033).
 * Uses MOCK_PRODUCTS[1] (Designer Sunglasses) as the wishlisted item.
 */
export const MOCK_WISH_PRODUCTS: WishProduct[] = [
  {
    id: 1,
    product: MOCK_PRODUCTS[1],
    product_variant: {
      id: 102,
      title: 'Designer Sunglasses - Gold',
      price: '599.00',
      sku: 'DSG-GLD-001',
    },
  },
];

/**
 * Mock wish products response for demo mode (SPEC-033).
 */
export const MOCK_WISH_PRODUCTS_RESPONSE: WishProductsResponse = {
  products: MOCK_WISH_PRODUCTS,
};

/**
 * Mock product detail for demo mode (SPEC-034).
 * Full product detail with variants, multiple images, description.
 */
export const MOCK_PRODUCT_DETAIL: ProductDetail = {
  id: 1,
  name: 'Classic Leather Handbag',
  price: '1,299.00',
  compare_at_price: '1,599.00',
  description: '<p>Crafted from the finest Italian leather, this timeless handbag features hand-stitched detailing and gold-tone hardware. The spacious interior includes a zippered pocket and card slots.</p><p>Dimensions: 30cm x 22cm x 12cm</p>',
  images: [
    'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600&h=600&fit=crop',
  ],
  image_url: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&h=300&fit=crop',
  product_variants: [
    {
      id: 101,
      name: 'Black / Medium',
      price: '1,299.00',
      compare_at_price: '1,599.00',
      inventory: 5,
      image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600&h=600&fit=crop',
      options: [{ name: 'Color', value: 'Black' }, { name: 'Size', value: 'Medium' }],
      add_to_cart_when_out_of_stock: false,
      product_options: [
        { id: 1, name: 'None', value: 'none', identifier: 'engraving', description: 'Engraving', price: '$0.00' },
        { id: 2, name: 'Initials', value: 'initials', identifier: 'engraving', description: 'Engraving', price: '$25.00' },
        { id: 3, name: 'Full Name', value: 'full_name', identifier: 'engraving', description: 'Engraving', price: '$45.00' },
        { id: 4, name: 'None', value: 'none', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$0.00' },
        { id: 5, name: 'Standard', value: 'standard', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$15.00' },
        { id: 6, name: 'Premium', value: 'premium', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$35.00' },
      ] as ProductDetailProductOption[],
    },
    {
      id: 102,
      name: 'Tan / Medium',
      price: '1,299.00',
      inventory: 3,
      image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&h=600&fit=crop',
      options: [{ name: 'Color', value: 'Tan' }, { name: 'Size', value: 'Medium' }],
      add_to_cart_when_out_of_stock: false,
      product_options: [
        { id: 7, name: 'None', value: 'none', identifier: 'engraving', description: 'Engraving', price: '$0.00' },
        { id: 8, name: 'Initials', value: 'initials', identifier: 'engraving', description: 'Engraving', price: '$25.00' },
        { id: 9, name: 'Full Name', value: 'full_name', identifier: 'engraving', description: 'Engraving', price: '$45.00' },
        { id: 10, name: 'None', value: 'none', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$0.00' },
        { id: 11, name: 'Standard', value: 'standard', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$15.00' },
        { id: 12, name: 'Premium', value: 'premium', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$35.00' },
      ] as ProductDetailProductOption[],
    },
    {
      id: 103,
      name: 'Black / Large',
      price: '1,499.00',
      inventory: 0,
      options: [{ name: 'Color', value: 'Black' }, { name: 'Size', value: 'Large' }],
      add_to_cart_when_out_of_stock: false,
      product_options: [
        { id: 13, name: 'None', value: 'none', identifier: 'engraving', description: 'Engraving', price: '$0.00' },
        { id: 14, name: 'Initials', value: 'initials', identifier: 'engraving', description: 'Engraving', price: '$25.00' },
        { id: 15, name: 'Full Name', value: 'full_name', identifier: 'engraving', description: 'Engraving', price: '$45.00' },
        { id: 16, name: 'None', value: 'none', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$0.00' },
        { id: 17, name: 'Standard', value: 'standard', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$15.00' },
        { id: 18, name: 'Premium', value: 'premium', identifier: 'gift_wrap', description: 'Gift Wrapping', price: '$35.00' },
      ] as ProductDetailProductOption[],
    },
  ],
  vendor: { name: 'Brand', value: 'Maison de Luxe' },
  tags: { name: 'Tags', value: ['leather', 'handbag', 'classic'] },
  type: { name: 'Type', value: 'Handbag' },
  collections: { name: 'Collection', value: ['Spring Collection'] },
};

/**
 * Mock product detail with NO variants for demo mode (SPEC-034).
 * Tests AC-18 edge case: no variants, long description for collapse test.
 */
export const MOCK_PRODUCT_DETAIL_NO_VARIANTS: ProductDetail = {
  id: 2,
  name: 'Silk Pocket Square',
  price: '189.00',
  description: '<p>This luxurious silk pocket square is handcrafted in Italy using centuries-old weaving techniques. The rich, vibrant pattern draws inspiration from Renaissance art, featuring intricate geometric motifs that complement any formal attire.</p><p>Made from 100% mulberry silk with hand-rolled edges, this accessory adds a distinguished touch to blazers and suit jackets. The fabric has a natural sheen that catches light beautifully, creating an elegant appearance at any event.</p><p><strong>Care Instructions:</strong> Dry clean only. Store flat or gently rolled. Avoid prolonged exposure to direct sunlight to preserve color vibrancy.</p><p>Dimensions: 33cm x 33cm. Weight: 25g. Origin: Como, Italy.</p>',
  images: [
    'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=600&h=600&fit=crop',
  ],
  image_url: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=400&h=300&fit=crop',
  product_variants: [],
  vendor: { name: 'Brand', value: 'Atelier Como' },
  tags: { name: 'Tags', value: ['silk', 'accessories', 'formal', 'italian'] },
  type: { name: 'Type', value: 'Accessory' },
  collections: { name: 'Collection', value: ['Gentlemen Essentials'] },
};

/**
 * Mock product detail response wrapper (SPEC-034).
 */
export const MOCK_PRODUCT_DETAIL_RESPONSE: ProductDetailResponse = {
  status: 'ok',
  response: MOCK_PRODUCT_DETAIL,
};

// ─── Combo Product Mock Data (SPEC-036) ─────────────────────────────────────

/**
 * Combo bundle definition for the mock parent product.
 * 2 child products: PC1001 (Size Selection) and PC1002 (Color Selection).
 */
const MOCK_COMBO_PRODUCTS_DEF: ComboProducts = {
  combo_product_discount: '15',
  ids: [1001, 1002],
  combo_configuration: {
    'PC1001': 'Size Selection',
    'PC1002': 'Color Selection',
  },
};

/**
 * MOCK_COMBO_PARENT_PRODUCT — parent combo bundle product (id=3).
 * Has a single default variant (not added to cart; only child variants are added).
 * Supports SPEC-036 AC-23 and AC-24 demo mode.
 */
export const MOCK_COMBO_PARENT_PRODUCT: ProductDetail = {
  id: 3,
  name: 'Luxury Combo Bundle',
  price: '615.00',
  compare_at_price: '745.00',
  description: '<p>This exclusive combo bundle pairs our finest athletic-fit blazer with matching trousers at a <strong>15% bundle discount</strong>. Each piece is crafted from premium four-way stretch fabric for unparalleled comfort and elegance.</p>',
  images: [
    'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&h=600&fit=crop',
  ],
  image_url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=300&fit=crop',
  product_variants: [
    {
      id: 300,
      name: 'Default Title',
      price: '615.00',
      compare_at_price: '745.00',
      inventory: 10,
      options: [],
      add_to_cart_when_out_of_stock: false,
    },
  ],
  vendor: { name: 'Brand', value: 'Atelier Prestige' },
  tags: { name: 'Tags', value: ['combo', 'bundle', 'suit', 'formal'] },
  type: { name: 'Type', value: 'Bundle' },
  collections: { name: 'Collection', value: ['Formal Wear'] },
  combo_products: MOCK_COMBO_PRODUCTS_DEF,
  product_code: 'COMBO-PARENT-001',
};

/**
 * MOCK_COMBO_CHILD_1 — child product for "Size Selection" (id=1001).
 * Has multiple size variants. product_code matches combo_configuration key "PC1001".
 * Child product name contains parent product name ("Luxury Combo Bundle Trousers" includes "Luxury Combo Bundle"),
 * which triggers auto-hide-by-name (AC-09) for any option whose value matches the child product name.
 */
export const MOCK_COMBO_CHILD_1: ProductDetail = {
  id: 1001,
  name: 'Luxury Combo Bundle Trousers',
  price: '285.00',
  description: '<p>Athletic-fit stretch trousers with four-way stretch fabric.</p>',
  images: [
    'https://images.unsplash.com/photo-1594938298603-c8148c4b5fa1?w=600&h=600&fit=crop',
  ],
  image_url: 'https://images.unsplash.com/photo-1594938298603-c8148c4b5fa1?w=400&h=300&fit=crop',
  product_variants: [
    {
      id: 1001_01,
      name: '30W / 30L',
      price: '285.00',
      inventory: 8,
      options: [{ name: 'Waist', value: '30W' }, { name: 'Inseam', value: '30L' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1001_02,
      name: '32W / 30L',
      price: '285.00',
      inventory: 12,
      options: [{ name: 'Waist', value: '32W' }, { name: 'Inseam', value: '30L' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1001_03,
      name: '34W / 32L',
      price: '285.00',
      inventory: 5,
      options: [{ name: 'Waist', value: '34W' }, { name: 'Inseam', value: '32L' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1001_04,
      name: '36W / 32L',
      price: '285.00',
      inventory: 0,
      options: [{ name: 'Waist', value: '36W' }, { name: 'Inseam', value: '32L' }],
      add_to_cart_when_out_of_stock: false,
    },
  ],
  vendor: { name: 'Brand', value: 'Atelier Prestige' },
  tags: { name: 'Tags', value: ['trousers', 'formal'] },
  type: { name: 'Type', value: 'Trousers' },
  collections: { name: 'Collection', value: ['Formal Wear'] },
  product_code: 'PC1001',
};

/**
 * MOCK_COMBO_CHILD_2 — child product for "Color Selection" (id=1002).
 * Has color + size variants. product_code matches combo_configuration key "PC1002".
 * Demonstrates different visible selector rendering from child 1.
 */
export const MOCK_COMBO_CHILD_2: ProductDetail = {
  id: 1002,
  name: 'Luxury Combo Bundle Blazer',
  price: '330.00',
  description: '<p>Athletic-fit stretch blazer with structured shoulders and slim lapels.</p>',
  images: [
    'https://images.unsplash.com/photo-1617137968427-85924c800a22?w=600&h=600&fit=crop',
  ],
  image_url: 'https://images.unsplash.com/photo-1617137968427-85924c800a22?w=400&h=300&fit=crop',
  product_variants: [
    {
      id: 1002_01,
      name: 'Navy / 38R',
      price: '330.00',
      inventory: 6,
      options: [{ name: 'Color', value: 'Navy' }, { name: 'Size', value: '38R' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1002_02,
      name: 'Navy / 40R',
      price: '330.00',
      inventory: 10,
      options: [{ name: 'Color', value: 'Navy' }, { name: 'Size', value: '40R' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1002_03,
      name: 'Charcoal / 38R',
      price: '330.00',
      inventory: 4,
      options: [{ name: 'Color', value: 'Charcoal' }, { name: 'Size', value: '38R' }],
      add_to_cart_when_out_of_stock: false,
    },
    {
      id: 1002_04,
      name: 'Charcoal / 42R',
      price: '330.00',
      inventory: 7,
      options: [{ name: 'Color', value: 'Charcoal' }, { name: 'Size', value: '42R' }],
      add_to_cart_when_out_of_stock: false,
    },
  ],
  vendor: { name: 'Brand', value: 'Atelier Prestige' },
  tags: { name: 'Tags', value: ['blazer', 'formal'] },
  type: { name: 'Type', value: 'Blazer' },
  collections: { name: 'Collection', value: ['Formal Wear'] },
  product_code: 'PC1002',
};

/**
 * Mock data for demo mode.
 * Conforms to JoinResponse type from SPEC-001/SPEC-011.
 * Scheduled 5 minutes in the future to simulate upcoming appointment.
 */
export const MOCK_JOIN_RESPONSE: JoinResponse = {
  response: {
    call_id: 99999,
    room_sid: 'RM_demo_room_sid',
    room_unique_name: 'demo-room',
    token: 'demo-video-token',
    conversation_sid: 'CH_demo_conversation_sid',
    chat_token: 'demo-chat-token',
    seller: {
      id: 1,
      full_name: 'Marie Laurent',
      image: undefined,
    },
    organization: {
      id: 1,
      name: 'Maison de Luxe',
    },
    scheduled_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
    customer_session: {
      id: 1,
      wss_sign: 'demo-wss-sign',
      access_token: 'demo-access-token',
      refresh_token: 'demo-refresh-token',
      service_id: 'demo-service-uuid',
    },
    recording_enabled: false, // SPEC-026/028: demo mode does not simulate transcription
  },
};
